'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Plastic Scan Apps in 2026: What They Do, How They Work, and Which One to Use"
      author="Claire Rowan"
      date="June 7, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/general.webp"
      heroImageAlt="Plastic scan apps 2026, how they work and which one to use"
    >
      <BlogLastUpdated iso="2026-06-07" formatted="June 7, 2026" />

      <BlogTLDR>
        A &ldquo;plastic scan app&rdquo; uses your phone camera to read a
        product&apos;s <strong>barcode or ingredient list</strong> and
        returns a risk score for plastic exposure, by mapping the
        product to a database of known polymers, packaging materials, and
        ingredient flags. No phone app does in-the-field chemistry on the
        plastic itself (that needs lab Raman or FTIR equipment costing
        $30,000+). What a good app does: it gives you the polymer, the
        use-condition risk, a 0–100 score, and the cleaner same-category
        swap, instantly, for free, on the shelf. Of the apps available in
        2026, three matter: <strong>MicroPlastics</strong> (packaging +
        ingredient polymer database, microplastic-specific scoring),
        <strong>Yuka</strong> (food and cosmetic additives, light plastic
        coverage), and <strong>Think Dirty</strong> (cosmetics-only
        ingredient scoring with some plastic-ingredient flagging).
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>No phone app can chemically analyse plastic in the field, that requires lab Raman or FTIR. What apps do is map a scanned barcode or ingredient list to a polymer-and-research database.</>,
          <>A good plastic scan app reports the polymer, the use-condition risk (heat, acidity, mechanical wear), a 0–100 score, and the cleaner alternative.</>,
          <>MicroPlastics is the only major app specifically built for microplastic exposure scoring across food packaging, cosmetics, and household products.</>,
          <>Yuka is broader (food and cosmetic additives) but lighter on plastic-specific scoring.</>,
          <>Think Dirty is cosmetics-only with rough flagging of plastic ingredients (polyethylene, PEG, carbomer, acrylates copolymer).</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What &ldquo;plastic scan app&rdquo; actually means
      </h2>
      <p>
        Three categories of phone app get called &ldquo;plastic scan
        apps,&rdquo; and they do very different things:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Barcode lookup apps.</strong> You point your camera at the barcode on the product. The app cross-references the barcode (UPC, EAN, GTIN) against a database that knows what polymer the packaging is, what ingredients are inside, and what the published research says about that product or category. This is the dominant model in 2026 because the barcode is the most reliable shelf identifier and the database does the heavy lifting.</li>
        <li><strong>Camera/AI image recognition apps.</strong> You photograph the product and a vision model attempts to identify the material from the image (clear PET bottle vs HDPE jug vs glass). Useful when there is no barcode, less reliable than database lookup.</li>
        <li><strong>Ingredient parser apps.</strong> You photograph or type the ingredient list and the app flags plastic-derived ingredients in cosmetics, personal care, and processed food. The standard list of flags: polyethylene, polypropylene, PEG and PEG derivatives, carbomer, acrylates copolymer, dimethicone, nylon, polyurethane.</li>
      </ul>
      <p>
        The best apps combine all three so you can scan whatever&apos;s
        in your hand without thinking about which mode to use.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What MicroPlastics specifically does
      </h2>
      <p>
        MicroPlastics is a barcode-and-ingredient scanner specifically
        focused on microplastic exposure (as opposed to general food or
        cosmetic ingredient scoring). On a scan, the app surfaces:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>The packaging polymer.</strong> What plastic the bottle, container, pouch, or capsule is made of (PET, HDPE, LDPE, PP, PS, PVC, multilayer co-extrusion, aluminum-with-liner).</li>
        <li><strong>The use-condition risk.</strong> Whether the product is intended for hot contact, acidic contact, repeated freeze–thaw, microwave heating, the conditions that drive most actual microplastic release.</li>
        <li><strong>A 0–100 risk score.</strong> Combining the polymer, the use condition, the published research on the product category, and the brand record.</li>
        <li><strong>The cleaner alternative.</strong> When one exists in the same category, a glass version of the same baby bottle line, an aluminum version of the same coffee pod, a silicone version of the same storage bag.</li>
        <li><strong>The ingredient flags for cosmetics and personal care.</strong> Polyethylene, PEG, carbomer, acrylates copolymer, dimethicone, nylon-12, polyurethane.</li>
        <li><strong>Linked research.</strong> The peer-reviewed studies the score is built on, so you can read the source if you want to.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How the scoring works under the hood
      </h2>
      <p>
        A 0–100 microplastic risk score is built from five inputs, each
        weighted to match what the published research actually shows
        drives exposure:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Polymer identity.</strong> Polystyrene and PVC score worst at heat; PP and HDPE score middle; LDPE scores middle at room temperature; aluminum-with-liner scores low; glass and steel score near zero.</li>
        <li><strong>Use-condition multiplier.</strong> Cold storage of dry food multiplies the polymer score by ~0.2. Hot contact above 60°C multiplies by 3–10×. Microwave use multiplies by 20–100× per the Hussain et al. (2023) numbers.</li>
        <li><strong>Category-specific research multiplier.</strong> Coffee pods, tea bags, paper cups, baby bottles, and bottled water all have category-specific particle counts in the literature that adjust the baseline.</li>
        <li><strong>Brand record.</strong> Recalls, supply-chain disclosures, third-party testing results, certification status.</li>
        <li><strong>Cleaner-alternative availability.</strong> A category with no realistic safer alternative scores differently from one with an easy swap available.</li>
      </ol>
      <p>
        The output is a 0–100 number where 0 is near-inert (glass jar of
        olive oil) and 100 is highest-risk consumer products (microwaved
        plastic takeaway in oil, K-Cup brewed into a paper cup).
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Plastic scan apps compared
      </h2>

      <BlogComparisonTable
        caption="Major scan apps in 2026 with plastic-relevant capability"
        columns={[
          { key: 'app', label: 'App' },
          { key: 'focus', label: 'Primary focus' },
          { key: 'mp', label: 'Microplastic scoring' },
          { key: 'fmt', label: 'Format coverage' },
          { key: 'cost', label: 'Cost' },
        ]}
        rows={[
          { app: 'MicroPlastics', focus: 'Microplastic exposure across packaging + ingredients', mp: 'Yes: 0–100 score with polymer + use-condition + research linkage', fmt: 'Food packaging, cosmetics, household, baby', cost: 'Free (3 scans included, then $7.99/mo)' },
          { app: 'Yuka', focus: 'Food and cosmetic ingredient additives', mp: 'Light, flags some plastic ingredients but no microplastic-specific score', fmt: 'Food + cosmetics', cost: 'Free, paid tier for offline mode' },
          { app: 'Think Dirty', focus: 'Cosmetic ingredient scoring', mp: 'Flags polyethylene, PEG, carbomer, no packaging coverage', fmt: 'Cosmetics only', cost: 'Free' },
          { app: 'EWG Healthy Living', focus: 'EWG-rated cosmetics and food', mp: 'No microplastic-specific scoring', fmt: 'Cosmetics + some food', cost: 'Free' },
          { app: 'INCI Beauty', focus: 'Cosmetic INCI list parsing', mp: 'Flags plastic ingredients in INCI lists', fmt: 'Cosmetics only', cost: 'Free' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What no plastic scan app can do (yet)
      </h2>
      <p>
        Three things to be honest about, because no app on the market
        does them and any app that claims to is bluffing:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Measure microplastic count in your specific bottle.</strong> That needs lab spectroscopy on a sample of the actual liquid. No phone camera does this.</li>
        <li><strong>Identify polymer type from a photo of an unlabelled container.</strong> Vision models get the general category right (clear bottle vs jug vs film) but not the polymer specifically.</li>
        <li><strong>Detect contamination events.</strong> Recalls, supply-chain issues, lot-specific problems, apps surface these once they become public, but cannot catch them in real time.</li>
      </ul>
      <p>
        What apps do well, and what makes them more useful than waiting
        for any of the above to become possible, is the everyday
        decision: which of the three brands on the shelf is the lower-
        plastic choice, and what is the cleaner alternative if none of
        them is good. That is most of what reduces exposure in practice.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        When scanning helps (and when it doesn&apos;t)
      </h2>
      <p>
        Scanning is most useful when:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>You&apos;re standing in a store comparing 2–4 specific products.</li>
        <li>You&apos;re auditing what&apos;s already in your kitchen / bathroom (one weekend, scan every item).</li>
        <li>You&apos;re cross-checking a brand claim (&ldquo;BPA-free&rdquo;, &ldquo;microplastic-free&rdquo;, &ldquo;plant-based plastic&rdquo;).</li>
        <li>You&apos;re shopping for a higher-risk category, baby bottles, food storage, cosmetics, single-serve coffee pods, water filters.</li>
      </ul>
      <p>
        Scanning is less useful when:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>You already know the category is high-risk and have a safer default (e.g. you already only buy glass-stored food, scanning every glass jar adds little).</li>
        <li>The product has no barcode (deli counter, farmers&apos; market, bulk bin).</li>
        <li>You&apos;re asking about use, not product (microwaving frequency, freeze-thaw cycles, brewing temperature, those are habit changes, not scan decisions).</li>
      </ul>

      <p>
        See also{' '}
        <Link href="/blog/how-to-test-for-microplastics-at-home" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          how to test for microplastics at home
        </Link>{' '}
        for what scanning replaces,{' '}
        <Link href="/blog/check-products-for-microplastics-before-buying" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          how to check products before buying
        </Link>
        , and the{' '}
        <Link href="/microplastic-free-products" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best microplastic-free products list
        </Link>
        .
      </p>

      <BlogAppCTA
        headline="Try a real plastic scan in 30 seconds"
        sub="Open the MicroPlastics app, point your camera at any product barcode in your house, and see the polymer, the use-condition risk, and the cleaner same-category alternative. Free, no signup."
        buttonLabel="Download MicroPlastics"
      />

      <BlogWhatTheAppChecks
        items={[
          <>Packaging polymer (PET, HDPE, LDPE, PP, PS, PVC, multilayer, aluminum-with-liner) from the barcode.</>,
          <>Use-condition flags, hot contact, acidic food, freezer use, microwave-safe-but-migrates.</>,
          <>Ingredient list parser for cosmetics: polyethylene, PEG, carbomer, acrylates copolymer, dimethicone, nylon-12.</>,
          <>Brand record, recalls, certifications, third-party test results when published.</>,
          <>A 0–100 score and the cleaner same-format alternative in the same product category.</>,
          <>Linked peer-reviewed research so you can verify the source behind the score.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'What is a plastic scan app?',
            answer:
              'A phone app that uses your camera to scan a product barcode or ingredient list and returns information about the plastic polymer in its packaging, the plastic ingredients in the product itself, and the microplastic exposure risk. It is a database lookup, not a chemistry test, no phone can do real polymer analysis.',
          },
          {
            question: 'How does a plastic scan app work?',
            answer:
              'Three mechanisms, usually combined. The app reads the barcode (UPC, EAN, GTIN) and looks up the product in a database that maps barcodes to polymer and ingredient information. Camera image recognition identifies material categories when no barcode is present. Ingredient parsing scans the ingredient list for plastic-derived chemicals like polyethylene, PEG, carbomer, and acrylates copolymer.',
          },
          {
            question: 'Can a phone app actually detect microplastics?',
            answer:
              'No phone can chemically analyse plastic in the field. Identifying a polymer chemically requires Raman, FTIR, or pyrolysis-GC/MS lab instruments that cost $30,000–$300,000. What a phone app does is map the scanned product to a database of known polymers and published research, which is the actionable information you need at the store anyway.',
          },
          {
            question: 'What is the best plastic scan app in 2026?',
            answer:
              'For microplastic exposure specifically, MicroPlastics is the only major app built around it, it covers packaging polymer, use-condition risk, cosmetic ingredients, and a 0–100 score per product. Yuka is broader for food and cosmetic ingredients but lighter on plastic-specific scoring. Think Dirty covers cosmetic ingredients only.',
          },
          {
            question: 'Is the MicroPlastics app free?',
            answer:
              'Yes. The free tier includes 3 scans, enough to check your biggest suspects. The Pro tier (3-day free trial, then $7.99/mo or $49.99/yr) removes the scan limit for full kitchen and bathroom audits.',
          },
          {
            question: 'Can I use a plastic scan app on cosmetics?',
            answer:
              'Yes: for cosmetics the app parses the ingredient list (INCI) for plastic-derived ingredients. Common flags: polyethylene, polypropylene, polyethylene terephthalate (PET), polyamide (nylon), PEG and PEG derivatives, carbomer, acrylates copolymer, dimethicone. These are the most common ingredient-level microplastics in personal care.',
          },
          {
            question: 'Will a plastic scan app help with food packaging?',
            answer:
              'Yes: this is the strongest use case. Scanning the barcode on a packaged food, beverage container, baby bottle, or coffee pod returns the packaging polymer, the published research on the product category, and a 0–100 risk score with the cleaner alternative when one exists.',
          },
          {
            question: 'How is a plastic scan app different from Yuka or Think Dirty?',
            answer:
              'Yuka focuses on food additives and cosmetic ingredient scoring; plastic packaging is a secondary signal in its scoring. Think Dirty is cosmetics-only and rates ingredients on a generic toxicity scale. A plastic scan app like MicroPlastics is built specifically around microplastic exposure, it scores packaging polymer and use conditions which Yuka and Think Dirty do not weight heavily.',
          },
        ]}
      />

      <BlogAppCTA
        headline="Audit one room of your house tonight"
        sub="The single highest-impact use of a scanner app is a one-evening audit of your kitchen or bathroom, scan every product, surface the worst three, replace them next shop. The MicroPlastics app is built for exactly that."
        buttonLabel="Start your audit"
      />

      <BlogSources
        items={[
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the Release of Microplastics and Nanoplastics from Plastic Containers and Reusable Food Pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'Proceedings of the National Academy of Sciences (PNAS)',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'World Health Organization',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles and potential implications for human health',
            publisher: 'WHO',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Restriction of intentionally added microplastics: REACH Annex XVII',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'US Food & Drug Administration',
            year: 2024,
            title: 'Food contact substance notifications: polymer resins',
            publisher: 'FDA',
            url: 'https://www.fda.gov/food/food-ingredients-packaging/food-contact-substances-fcs',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
